/**
 * Login Page — GE Lines Self-Service Portal
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useStorefront } from '@/context/StorefrontContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';
import { TenantBrandMark } from '@/components/TenantBrandMark';
import { resolvePortalTheme } from '@/lib/portal-theme';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();
  const { brand, visual, host, storefrontBaseUrl, experienceMode } = useStorefront();
  const portalTheme = resolvePortalTheme(brand.portalThemeTemplate, visual);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/portal';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-8rem)] flex items-center justify-center py-12" style={{ ...portalTheme.cssVars, ...portalTheme.shellBackground }}>
      <div className="pointer-events-none absolute inset-0" style={portalTheme.textureOverlay} />
      <div className="w-full max-w-md mx-auto px-4">
        <div className="relative rounded-2xl shadow-xl p-8 animate-slide-up" style={portalTheme.panelSurface}>
          <div className="text-center mb-8 space-y-4">
            <TenantBrandMark
              brand={brand}
              size="lg"
              subtitle={brand.portalLabel}
              detail={brand.legalName}
              stacked
            />
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium text-brand-700" style={portalTheme.chipSurface}>
              <ShieldCheck className="w-3.5 h-3.5" />
              Secure tenant access for {host}
            </div>
            <p className="text-slate-500 text-sm">
              Sign in to continue into the {brand.brandShortName} workspace.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email / Login ID
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-brand-500 focus:outline-none text-sm"
                  placeholder="you@company.com or login ID"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 rounded-lg border border-slate-200 focus:border-brand-500 focus:outline-none text-sm"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" />
                <span className="text-slate-600">Remember me</span>
              </label>
              <a href={`mailto:${brand.primaryEmail}`} className="text-brand-600 hover:text-brand-700">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-brand-700 text-white font-medium rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium">
              Contact us
            </Link>
          </div>
          {experienceMode === 'portal' && storefrontBaseUrl && (
            <div className="mt-3 text-center text-xs text-slate-400">
              Need company info first?{' '}
              <a href={storefrontBaseUrl} className="text-brand-600 hover:text-brand-700 font-medium">
                Visit {brand.brandShortName} website
              </a>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-5">
          By signing in you agree to {brand.brandShortName} terms of service.
        </p>
      </div>
    </div>
  );
}
